import React from 'react';

import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { getPosHandlerNode } from '@atlaskit/editor-common/types';
import type { Node as PmNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import { isAgentManagedExtension } from '../../common/utils';
import { AgentManagedBodiedExtensionRenderer } from '../../extension/render-bodied-extension';

import { attachBodyClickSelection, createNodeViewDom } from './nodeview-dom';

interface NodeViewArgs {
	portalProviderAPI: PortalProviderAPI;
	providerFactory?: ProviderFactory;
}

const buildPortalKey = (node: PmNode): string =>
	`agent-managed-extension-${(node.attrs.localId as string) ?? Math.random().toString(36)}`;

/**
 * Registry of active agent-managed-extension node views.
 * Used by the plugin's `view.update()` to trigger re-renders when plugin
 * state changes (e.g. config open/close), since ProseMirror only calls
 * `NodeView.update()` when the underlying node itself changes.
 */
const activeNodeViews = new Set<AgentManagedExtensionNodeView>();

/**
 * Re-renders all active agent-managed-extension node views. Called from
 * the plugin's `view.update()` when state changes that affect rendering.
 */
export const rerenderAllNodeViews = (): void => {
	for (const nv of activeNodeViews) {
		nv.renderReact();
	}
};

/**
 * Marks a specific node view as selected/deselected
 */
export const setNodeViewSelectedByLocalId = (localId: string, selected: boolean): void => {
	for (const nv of activeNodeViews) {
		if (nv.getLocalId() === localId) {
			nv.setSelected(selected);
		}
	}
};

/**
 * Custom nodeView that omits a `contentDOM` to keep the body
 * read-only and renders bespoke chrome (inline Rovo lozenge,
 * hover/focus-only border) instead of the framework's default.
 */
class AgentManagedExtensionNodeView implements NodeView {
	public dom: HTMLDivElement;

	private node: PmNode;
	private portalProviderAPI: PortalProviderAPI;
	private providerFactory: ProviderFactory | undefined;
	private mountPoint: HTMLDivElement;
	private portalKey: string;
	private detachBodyClick: () => void;
	private selected = false;

	constructor(node: PmNode, view: EditorView, getPos: getPosHandlerNode, args: NodeViewArgs) {
		this.node = node;
		this.portalProviderAPI = args.portalProviderAPI;
		this.providerFactory = args.providerFactory;

		const { root, mountPoint } = createNodeViewDom(view.dom.ownerDocument, node);
		this.dom = root;
		this.mountPoint = mountPoint;
		this.portalKey = buildPortalKey(node);

		this.detachBodyClick = attachBodyClickSelection(view, this.dom, getPos);
		activeNodeViews.add(this);
		this.renderReact();
	}

	getLocalId(): string {
		return (this.node.attrs.localId as string) ?? '';
	}

	setSelected(selected: boolean) {
		this.selected = selected;
	}

	renderReact = () => {
		const element = (
			<AgentManagedBodiedExtensionRenderer
				node={this.node}
				providerFactory={this.providerFactory}
				isSelected={this.selected}
			/>
		);
		this.portalProviderAPI.render(() => element, this.mountPoint, this.portalKey);
	};

	update(node: PmNode): boolean {
		if (node.type !== this.node.type) {
			return false;
		}
		// Refuse to re-use this nodeView for a non-agent-managed block;
		// ProseMirror will then ask the factory for a fresh nodeView.
		if (!isAgentManagedExtension(node)) {
			return false;
		}
		// Force a remount when the localId changes — prevents stale
		// React state from leaking across instances.
		if (node.attrs.localId !== this.node.attrs.localId) {
			return false;
		}
		if (node === this.node) {
			return true;
		}
		this.dom.setAttribute('data-local-id', node.attrs.localId ?? '');
		this.node = node;
		this.renderReact();
		return true;
	}

	selectNode() {
		this.selected = true;
		this.renderReact();
	}

	deselectNode() {
		this.selected = false;
		this.renderReact();
	}

	destroy() {
		activeNodeViews.delete(this);
		this.detachBodyClick();
		this.portalProviderAPI.remove(this.portalKey);
	}

	stopEvent(): boolean {
		return true;
	}

	ignoreMutation(): boolean {
		return true;
	}
}

export const getAgentManagedExtensionNodeView =
	(args: NodeViewArgs) =>
	(node: PmNode, view: EditorView, getPos: getPosHandlerNode): NodeView => {
		// Only handle agent-managed bodied extensions; let the framework's
		// default `bodiedExtension` nodeView render every other one.
		if (!isAgentManagedExtension(node)) {
			return null as unknown as NodeView;
		}
		return new AgentManagedExtensionNodeView(node, view, getPos, args);
	};
